import { useCallback, useEffect, useState } from "react";

import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from "axios";

import { HttpMethodProps } from "../../lib/types";
import { HttpCodes } from "../../lib/data";

interface RequestOptions {
  method: HttpMethodProps;
  route: string;
  data?: any;
  showError?: boolean;
  doNotUseAuthorization?: boolean;
}
export function usePerformRequest<Type>({
  method,
  route,
  data: body,
  showError = true,
  doNotUseAuthorization = false,
}: RequestOptions) {
  const [data, setData] = useState<Type>();
  const [response, setResponse] = useState<AxiosResponse>();
  const [error, setError] = useState<AxiosError>();
  const [status, setStatus] = useState<number | string>("undefined");
  const [isLoading, setLoading] = useState<boolean>(false);
  const reloadData = useCallback(async () => {
    const token = localStorage.getItem("t");
    const headers: any = {};
    if (!doNotUseAuthorization && token) {
      headers.Authorization = `Bearer ${token}`;
    }
    const config: AxiosRequestConfig = {
      headers,
      method,
      data: body,
      url: route,
    };
    setLoading(true);
    const r: any = await axios
      .request(config)
      .catch((e: AxiosError<Type, Type>) => {
        setError(e);
        setStatus(e.response?.status as number);

        if (e?.status === HttpCodes.Forbidden) {
          // @ts-ignore
          if (e?.response && e.response?.data && e.response.data?.data[0]) {
            if (showError) {
              // @ts-ignore
              toast.error(e.response.data?.data[0]);
            }
          } else {
            if (showError) {
              // @ts-ignore
              toast.error(e.response?.data?.message);
            }
          }
        }
        return {
          status: e.response?.status,
        };
      });
    setResponse(r as AxiosResponse);
    setStatus(r.status);
    setLoading(false);
    setData(r?.data ?? undefined);
  }, [body, doNotUseAuthorization, method, route, showError]);
  useEffect(() => {
    reloadData().catch(() => setLoading(false));
  }, [reloadData]);

  return { data, response, isLoading, reloadData, status, error };
}

interface PerformRequestOptions {
  method: HttpMethodProps;
  data?: any;
  route: string;
  headers?: any;
  doNotUseAuthorization?: boolean;
  onError?: () => any;
}

export async function PerformRequest<Type>({
  method,
  data,
  route,
  headers,
  doNotUseAuthorization,
  onError,
}: PerformRequestOptions) {
  const token = localStorage.getItem("t");

  const requestHeaders = doNotUseAuthorization
    ? { ...headers }
    : { Authorization: `Bearer ${token}`, ...headers };
  const config = {
    method,
    data,
    url: route,
    headers: requestHeaders,
  };

  const r: any = await axios.request(config).catch((e: AxiosError) => {
    if (onError) onError();
    return {
      status: e.response?.status,
      message: (e?.response?.data as any)?.message,
      data: (e?.response?.data as any)?.data,
    };
  });

  return {
    status: r.status as number,
    data: r?.data as Type,
    message: r?.message ?? undefined,
  };
}
